import { FC, PropsWithChildren } from "react";
import { Box } from "@mui/material";
import { TECHNO_COLORS } from "../../theme";

const { cyan, magenta, violet, acid } = TECHNO_COLORS;
const GRADIENT_ID = "techno-logo-gradient";

// Where the twinkles sit around the logo, as percentages of its box.
const SPARKS = [
  { top: "12%", left: "4%", size: 14, delay: 0, color: cyan },
  { top: "70%", left: "18%", size: 9, delay: 0.4, color: "#FFFFFF" },
  { top: "8%", left: "52%", size: 11, delay: 0.9, color: acid },
  { top: "74%", left: "70%", size: 13, delay: 0.25, color: magenta },
  { top: "18%", left: "92%", size: 10, delay: 0.65, color: "#FFFFFF" }
];

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The MFT logo in techno mode: its single orange fill swapped for a neon gradient that runs
 * across the letters, a glow, and a few twinkling sparkles around it. Wraps the normal logo
 * rather than shipping a second SVG, so the mark itself never drifts from the real one.
 *
 * The gradient lives in a zero-size inline SVG; url(#id) fills resolve across inline SVGs in
 * the same document, and the logo is inlined by svgr. Its gradientUnits span the logo's own
 * 1920 wide viewBox.
 */
export const TechnoLogo: FC<PropsWithChildren> = ({ children }) => (
  <Box
    className="techno-motion"
    sx={{
      position: "relative",
      display: "flex",
      alignItems: "center",
      "& > svg:not([aria-hidden])": {
        filter: `drop-shadow(0 0 6px ${magenta}) drop-shadow(0 0 14px rgba(0, 240, 255, 0.6))`
      },
      "& > svg:not([aria-hidden]) :is(path, polygon, rect, circle, ellipse)": {
        fill: `url(#${GRADIENT_ID}) !important`
      }
    }}
  >
    <svg
      aria-hidden
      width="0"
      height="0"
      style={{ position: "absolute", width: 0, height: 0 }}
    >
      <defs>
        <linearGradient
          id={GRADIENT_ID}
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="1920"
          y2="0"
          spreadMethod="reflect"
        >
          <stop offset="0%" stopColor={magenta} />
          <stop offset="35%" stopColor={cyan} />
          <stop offset="65%" stopColor={acid} />
          <stop offset="100%" stopColor={violet} />
          {!prefersReducedMotion() && (
            <animateTransform
              attributeName="gradientTransform"
              type="translate"
              from="0 0"
              to="1920 0"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </linearGradient>
      </defs>
    </svg>
    {children}
    {SPARKS.map((spark, i) => (
      <Box
        key={i}
        component="span"
        aria-hidden
        sx={{
          position: "absolute",
          top: spark.top,
          left: spark.left,
          fontSize: spark.size,
          lineHeight: 1,
          color: spark.color,
          textShadow: `0 0 6px ${spark.color}, 0 0 12px ${spark.color}`,
          pointerEvents: "none",
          animation: `techno-twinkle 1.2s ease-in-out ${spark.delay}s infinite`
        }}
      >
        ✦
      </Box>
    ))}
  </Box>
);
